/**
 * Multi-resolution Goldberg hexsphere — deterministic tessellation of the unit
 * sphere at one of three frequencies. 12 pentagons (at the original
 * icosahedron vertices) + the rest hexagons. Shared between server and client
 * so `hex_id` is universal as long as both agree on the planet's frequency.
 *
 *   G(6)  → 362 cells  (chunky asteroid feel, small planets)
 *   G(8)  → 642 cells  (medium — the default)
 *   G(10) → 1002 cells (detailed, large planets)
 *
 * Frequency for a planet is derived from its `size_multiplier` via
 * `frequencyForSize`. Same multiplier always picks the same frequency, so
 * `hex_id` resolution is stable across server boots.
 *
 * Build pipeline:
 *   1. Icosahedron (12 vertices, 20 triangle faces).
 *   2. Frequency-M barycentric subdivision of each face + dedup of shared
 *      edge/vertex points. Project all to the unit sphere.
 *   3. For each subdivided vertex, build the cell = polygon whose corners are
 *      the centroids of all triangles touching that vertex.
 *
 * Each frequency is built lazily on first request and cached for the process
 * lifetime.
 */

// =================== CONFIG ===================

/** Supported subdivision frequencies. G(M,0) → 10·M² + 2 cells. */
export type HexFrequency = 6 | 8 | 10;

/** Backwards-compat default for callers that don't yet pass a frequency. */
export const HEXSPHERE_FREQUENCY: HexFrequency = 8;

/** Total cell count for the chosen frequency. */
export function cellCountForFrequency(freq: HexFrequency): number {
  return 10 * freq * freq + 2;
}

/** Legacy export — count for the default frequency. */
export const HEXSPHERE_CELL_COUNT = cellCountForFrequency(HEXSPHERE_FREQUENCY);

/** Max cell count across all supported frequencies. Used for DB CHECK ranges. */
export const HEXSPHERE_MAX_CELL_COUNT = cellCountForFrequency(10);

/**
 * Derive the hexsphere frequency for a planet given its `size_multiplier`
 * (range 0.5 .. 2.0 per `PLANET_BALANCE.PROFILE_SIZE_*`). Thresholds chosen so
 * roughly 1/3 of profiles land in each bucket assuming a uniform distribution
 * over [0.5, 2.0].
 *
 *   size < 0.85       → G(6)   (chunky, ~362 cells)
 *   0.85 ≤ size < 1.4 → G(8)   (~642 cells, the existing default)
 *   size ≥ 1.4        → G(10)  (detailed, ~1002 cells)
 *
 * The function is the single source of truth: server + client both call it
 * with the same `size_multiplier` so the chosen mesh is universal.
 */
export function frequencyForSize(sizeMultiplier: number): HexFrequency {
  if (sizeMultiplier < 0.85) return 6;
  if (sizeMultiplier < 1.4) return 8;
  return 10;
}

// =================== TYPES ===================

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface HexCell {
  /** Stable index 0..N-1 (same across server/client runs). */
  id: number;
  /** Centroid on the unit sphere (the point we sample the noise field at). */
  center: Vec3;
  /** Polygon corners on the unit sphere, ordered CCW when viewed from outside. */
  corners: Vec3[];
  /** Neighbor cell ids (5 for pentagons, 6 for hexagons). */
  neighbors: number[];
  /** True iff this cell has 5 corners (one of the 12 pentagons). */
  isPentagon: boolean;
  /** Centroid latitude in degrees, in [-90, 90]. */
  lat: number;
  /** Centroid longitude in degrees, in [-180, 180]. */
  lon: number;
}

export interface HexSphereData {
  cells: HexCell[];
  count: number;
  pentagonCount: number;
  hexagonCount: number;
}

// =================== VECTOR HELPERS ===================

function vec(x: number, y: number, z: number): Vec3 { return { x, y, z }; }

function dot(a: Vec3, b: Vec3): number { return a.x * b.x + a.y * b.y + a.z * b.z; }

function cross(a: Vec3, b: Vec3): Vec3 {
  return vec(
    a.y * b.z - a.z * b.y,
    a.z * b.x - a.x * b.z,
    a.x * b.y - a.y * b.x,
  );
}

function normalize(a: Vec3): Vec3 {
  const len = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
  if (len < 1e-12) return vec(0, 0, 1);
  const inv = 1 / len;
  return vec(a.x * inv, a.y * inv, a.z * inv);
}

// =================== ICOSAHEDRON BASE ===================

const PHI = (1 + Math.sqrt(5)) / 2;

/** 12 base icosahedron vertices (un-normalized; normalized at build time). */
const ICO_VERTS_RAW: ReadonlyArray<readonly [number, number, number]> = [
  [-1,  PHI, 0], [ 1,  PHI, 0], [-1, -PHI, 0], [ 1, -PHI, 0],
  [ 0, -1,  PHI], [ 0,  1,  PHI], [ 0, -1, -PHI], [ 0,  1, -PHI],
  [ PHI, 0, -1], [ PHI, 0,  1], [-PHI, 0, -1], [-PHI, 0,  1],
];

/** 20 base triangle faces (CCW from outside the sphere). */
const ICO_FACES: ReadonlyArray<readonly [number, number, number]> = [
  [ 0, 11,  5], [ 0,  5,  1], [ 0,  1,  7], [ 0,  7, 10], [ 0, 10, 11],
  [ 1,  5,  9], [ 5, 11,  4], [11, 10,  2], [10,  7,  6], [ 7,  1,  8],
  [ 3,  9,  4], [ 3,  4,  2], [ 3,  2,  6], [ 3,  6,  8], [ 3,  8,  9],
  [ 4,  9,  5], [ 2,  4, 11], [ 6,  2, 10], [ 8,  6,  7], [ 9,  8,  1],
];

// =================== FREQUENCY HELPERS ===================

function assertFrequency(freq: HexFrequency): void {
  if (freq !== 6 && freq !== 8 && freq !== 10) {
    throw new Error(`Unsupported hexsphere frequency: ${freq}. Use 6, 8, or 10.`);
  }
}

// =================== GENERATION ===================

interface BuildState {
  positions: Vec3[];
  keyToIndex: Map<string, number>;
  triangles: number[][];
}

function pointKey(p: Vec3): string {
  // 7 decimals = ~10 nm on a unit sphere. Enough to dedup shared edge/vertex
  // points across adjacent faces without false positives.
  return `${p.x.toFixed(7)},${p.y.toFixed(7)},${p.z.toFixed(7)}`;
}

function getOrAddPoint(state: BuildState, rawPoint: Vec3): number {
  const onSphere = normalize(rawPoint);
  const k = pointKey(onSphere);
  const existing = state.keyToIndex.get(k);
  if (existing !== undefined) return existing;
  const idx = state.positions.length;
  state.positions.push(onSphere);
  state.keyToIndex.set(k, idx);
  return idx;
}

/** Subdivide the icosahedron at frequency M, projecting to the unit sphere. */
function buildSubdividedIcosahedron(M: number): BuildState {
  const icoVerts = ICO_VERTS_RAW.map(([x, y, z]) => normalize(vec(x, y, z)));
  const state: BuildState = {
    positions: [],
    keyToIndex: new Map(),
    triangles: [],
  };

  for (const [ia, ib, ic] of ICO_FACES) {
    const A = icoVerts[ia], B = icoVerts[ib], C = icoVerts[ic];

    // grid[i][j] = point index at barycentric (i, j, M - i - j).
    const grid: number[][] = [];
    for (let i = 0; i <= M; i++) {
      grid[i] = [];
      for (let j = 0; j <= M - i; j++) {
        const k = M - i - j;
        const p = vec(
          (A.x * i + B.x * j + C.x * k) / M,
          (A.y * i + B.y * j + C.y * k) / M,
          (A.z * i + B.z * j + C.z * k) / M,
        );
        grid[i][j] = getOrAddPoint(state, p);
      }
    }

    // Emit sub-triangles. Up-pointing triangles for every (i, j), down-pointing
    // when there's room (i.e., j < M - i - 1).
    for (let i = 0; i < M; i++) {
      for (let j = 0; j < M - i; j++) {
        state.triangles.push([grid[i][j], grid[i + 1][j], grid[i][j + 1]]);
        if (j < M - i - 1) {
          state.triangles.push([grid[i + 1][j], grid[i + 1][j + 1], grid[i][j + 1]]);
        }
      }
    }
  }

  return state;
}

/** Sort polygon corners CCW around `center` (when viewed from outside). */
function sortAroundCenter(corners: Vec3[], center: Vec3): Vec3[] {
  if (corners.length <= 2) return corners.slice();
  // Build a tangent basis orthogonal to `center` on the unit sphere.
  const up = Math.abs(center.z) < 0.95 ? vec(0, 0, 1) : vec(1, 0, 0);
  const tU = normalize(cross(up, center));
  const tV = normalize(cross(center, tU));
  const withAngle = corners.map((p) => ({
    p,
    angle: Math.atan2(dot(p, tV), dot(p, tU)),
  }));
  withAngle.sort((a, b) => a.angle - b.angle);
  return withAngle.map((x) => x.p);
}

function buildHexSphere(freq: HexFrequency = HEXSPHERE_FREQUENCY): HexSphereData {
  const M = freq;
  const state = buildSubdividedIcosahedron(M);
  const { positions, triangles } = state;

  // For each vertex, indices of triangles touching it.
  const incidentTris: number[][] = positions.map(() => []);
  for (let t = 0; t < triangles.length; t++) {
    const [a, b, c] = triangles[t];
    incidentTris[a].push(t);
    incidentTris[b].push(t);
    incidentTris[c].push(t);
  }

  // Triangle centroids on the unit sphere — these are the corners of the dual cells.
  const triCentroids: Vec3[] = triangles.map(([a, b, c]) => {
    const pa = positions[a], pb = positions[b], pc = positions[c];
    return normalize(vec(
      (pa.x + pb.x + pc.x) / 3,
      (pa.y + pb.y + pc.y) / 3,
      (pa.z + pb.z + pc.z) / 3,
    ));
  });

  // Neighbor relation: two cells are adjacent iff their underlying subdivided
  // vertices share an edge in any triangle.
  const neighborSets: Set<number>[] = positions.map(() => new Set<number>());
  for (const [a, b, c] of triangles) {
    neighborSets[a].add(b); neighborSets[a].add(c);
    neighborSets[b].add(a); neighborSets[b].add(c);
    neighborSets[c].add(a); neighborSets[c].add(b);
  }

  const cells: HexCell[] = positions.map((center, id) => {
    const ts = incidentTris[id];
    const cornersUnsorted = ts.map((ti) => triCentroids[ti]);
    const corners = sortAroundCenter(cornersUnsorted, center);
    const neighbors = Array.from(neighborSets[id]).sort((a, b) => a - b);
    const lat = (Math.asin(Math.max(-1, Math.min(1, center.z))) * 180) / Math.PI;
    const lon = (Math.atan2(center.y, center.x) * 180) / Math.PI;
    return {
      id,
      center,
      corners,
      neighbors,
      isPentagon: ts.length === 5,
      lat,
      lon,
    };
  });

  const pentagonCount = cells.reduce((n, c) => n + (c.isPentagon ? 1 : 0), 0);
  return {
    cells,
    count: cells.length,
    pentagonCount,
    hexagonCount: cells.length - pentagonCount,
  };
}

// =================== PUBLIC API ===================
//
// Every function takes an optional `freq` parameter so the caller can address
// a planet's specific mesh. Default = HEXSPHERE_FREQUENCY for backwards
// compatibility with code paths that haven't been threaded yet.

const cache = new Map<HexFrequency, HexSphereData>();

/** Returns the hexsphere data for the requested frequency. Built lazily, cached forever. */
export function getHexSphere(freq: HexFrequency = HEXSPHERE_FREQUENCY): HexSphereData {
  assertFrequency(freq);
  let hit = cache.get(freq);
  if (!hit) {
    hit = buildHexSphere(freq);
    cache.set(freq, hit);
  }
  return hit;
}

/** Convert (lat, lon) in degrees to the closest hex cell id by greatest dot product. */
export function latLonToHexId(
  lat: number,
  lon: number,
  freq: HexFrequency = HEXSPHERE_FREQUENCY,
): number {
  const phi = (lat * Math.PI) / 180;
  const lam = (lon * Math.PI) / 180;
  const cp = Math.cos(phi);
  const px = cp * Math.cos(lam);
  const py = cp * Math.sin(lam);
  const pz = Math.sin(phi);

  const { cells } = getHexSphere(freq);
  let best = 0;
  let bestDot = -2;
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i].center;
    const d = px * c.x + py * c.y + pz * c.z;
    if (d > bestDot) {
      bestDot = d;
      best = i;
    }
  }
  return best;
}

/** Hex id → centroid (lat, lon) in degrees. */
export function hexIdToLatLon(
  id: number,
  freq: HexFrequency = HEXSPHERE_FREQUENCY,
): { lat: number; lon: number } {
  const cells = getHexSphere(freq).cells;
  if (id < 0 || id >= cells.length) {
    throw new Error(`hexIdToLatLon: id ${id} out of range for G(${freq})`);
  }
  return { lat: cells[id].lat, lon: cells[id].lon };
}

/** Neighbor ids of a hex (5 for pentagons, 6 otherwise). */
export function neighborsOf(
  id: number,
  freq: HexFrequency = HEXSPHERE_FREQUENCY,
): number[] {
  return getHexSphere(freq).cells[id].neighbors;
}

/** BFS-reachable hexes within `k` graph-distance steps. Includes the source. */
export function withinRings(
  id: number,
  k: number,
  freq: HexFrequency = HEXSPHERE_FREQUENCY,
): number[] {
  if (k < 0) return [];
  const cells = getHexSphere(freq).cells;
  const visited = new Uint8Array(cells.length);
  visited[id] = 1;
  let frontier = [id];
  const all: number[] = [id];
  for (let step = 0; step < k; step++) {
    const next: number[] = [];
    for (const f of frontier) {
      for (const nb of cells[f].neighbors) {
        if (!visited[nb]) {
          visited[nb] = 1;
          next.push(nb);
          all.push(nb);
        }
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }
  return all;
}

/** Hexes at exactly graph-distance `k` from `id`. */
export function ringAt(
  id: number,
  k: number,
  freq: HexFrequency = HEXSPHERE_FREQUENCY,
): number[] {
  if (k < 0) return [];
  if (k === 0) return [id];
  const cells = getHexSphere(freq).cells;
  const visited = new Uint8Array(cells.length);
  visited[id] = 1;
  let frontier = [id];
  for (let step = 0; step < k; step++) {
    const next: number[] = [];
    for (const f of frontier) {
      for (const nb of cells[f].neighbors) {
        if (!visited[nb]) {
          visited[nb] = 1;
          next.push(nb);
        }
      }
    }
    if (next.length === 0) return [];
    frontier = next;
  }
  return frontier;
}

/** Graph-distance (BFS hops) between two hexes. */
export function hexGraphDistance(
  idA: number,
  idB: number,
  freq: HexFrequency = HEXSPHERE_FREQUENCY,
): number {
  if (idA === idB) return 0;
  const cells = getHexSphere(freq).cells;
  const visited = new Uint8Array(cells.length);
  visited[idA] = 1;
  let frontier = [idA];
  let dist = 0;
  while (frontier.length > 0) {
    dist += 1;
    const next: number[] = [];
    for (const f of frontier) {
      for (const nb of cells[f].neighbors) {
        if (nb === idB) return dist;
        if (!visited[nb]) {
          visited[nb] = 1;
          next.push(nb);
        }
      }
    }
    frontier = next;
  }
  return -1; // disconnected — shouldn't happen on a sphere
}

/** Great-circle (angular) distance between two hex centroids, in radians. */
export function angularDistance(
  idA: number,
  idB: number,
  freq: HexFrequency = HEXSPHERE_FREQUENCY,
): number {
  const cells = getHexSphere(freq).cells;
  const a = cells[idA].center;
  const b = cells[idB].center;
  return Math.acos(Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z)));
}

// =================== VALIDATION ===================

export interface HexSphereValidationReport {
  ok: boolean;
  errors: string[];
  stats: {
    totalCells: number;
    pentagons: number;
    hexagons: number;
    neighborSymmetric: boolean;
    pentNeighborsAlways5: boolean;
    hexNeighborsAlways6: boolean;
    minNeighborCount: number;
    maxNeighborCount: number;
  };
}

/** Run invariant checks on the built mesh at a given frequency. */
export function validateHexSphere(
  freq: HexFrequency = HEXSPHERE_FREQUENCY,
): HexSphereValidationReport {
  const errors: string[] = [];
  const { cells, count, pentagonCount, hexagonCount } = getHexSphere(freq);
  const expected = cellCountForFrequency(freq);

  if (count !== expected) {
    errors.push(`G(${freq}): expected ${expected} cells, got ${count}`);
  }
  if (pentagonCount !== 12) {
    errors.push(`G(${freq}): expected 12 pentagons, got ${pentagonCount}`);
  }
  if (hexagonCount !== expected - 12) {
    errors.push(`G(${freq}): expected ${expected - 12} hexagons, got ${hexagonCount}`);
  }

  let neighborSymmetric = true;
  let pentNeighborsAlways5 = true;
  let hexNeighborsAlways6 = true;
  let minNeighborCount = Infinity;
  let maxNeighborCount = -Infinity;

  for (const c of cells) {
    minNeighborCount = Math.min(minNeighborCount, c.neighbors.length);
    maxNeighborCount = Math.max(maxNeighborCount, c.neighbors.length);

    if (c.isPentagon && c.neighbors.length !== 5) pentNeighborsAlways5 = false;
    if (!c.isPentagon && c.neighbors.length !== 6) hexNeighborsAlways6 = false;

    for (const nb of c.neighbors) {
      if (!cells[nb].neighbors.includes(c.id)) {
        neighborSymmetric = false;
      }
    }
  }

  if (!neighborSymmetric) errors.push('neighbor relation is not symmetric');
  if (!pentNeighborsAlways5) errors.push('some pentagons do not have exactly 5 neighbors');
  if (!hexNeighborsAlways6) errors.push('some hexagons do not have exactly 6 neighbors');

  return {
    ok: errors.length === 0,
    errors,
    stats: {
      totalCells: count,
      pentagons: pentagonCount,
      hexagons: hexagonCount,
      neighborSymmetric,
      pentNeighborsAlways5,
      hexNeighborsAlways6,
      minNeighborCount,
      maxNeighborCount,
    },
  };
}
